// src/pages/admin/AdminFaqsPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminFaqsApi } from '@/api/services'
import toast from 'react-hot-toast'
import { HelpCircle, Plus, Pencil, Trash2, GripVertical, X } from 'lucide-react'

const CATEGORIES = ['general', 'orders', 'payments', 'shipping', 'returns', 'account', 'bulk_orders', 'suppliers']

export default function AdminFaqsPage() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(null) // null | 'new' | faq object
  const [form, setForm] = useState({ category: 'general', question: '', answer: '', sort_order: 0, is_published: true })

  const { data, isLoading } = useQuery({
    queryKey: ['admin-faqs'],
    queryFn: () => adminFaqsApi.list().then(r => r.data),
  })

  const saveMut = useMutation({
    mutationFn: (d) => editing === 'new' ? adminFaqsApi.create(d) : adminFaqsApi.update(editing.id, d),
    onSuccess: () => {
      toast.success(editing === 'new' ? 'FAQ created' : 'FAQ updated')
      setEditing(null)
      qc.invalidateQueries({ queryKey: ['admin-faqs'] })
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to save'),
  })

  const deleteMut = useMutation({
    mutationFn: (id) => adminFaqsApi.delete(id),
    onSuccess: () => {
      toast.success('FAQ deleted')
      qc.invalidateQueries({ queryKey: ['admin-faqs'] })
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to delete'),
  })

  const faqs = data?.data ?? data ?? []

  const openEdit = (faq) => {
    setEditing(faq)
    setForm({
      category: faq.category || 'general',
      question: faq.question || '',
      answer: faq.answer || '',
      sort_order: faq.sort_order || 0,
      is_published: faq.is_published ?? true,
    })
  }

  const openNew = () => {
    setEditing('new')
    setForm({ category: 'general', question: '', answer: '', sort_order: 0, is_published: true })
  }

  // Group by category
  const grouped = faqs.reduce((acc, faq) => {
    const cat = faq.category || 'general'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(faq)
    return acc
  }, {})

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">FAQ Management</h1>
        <button
          onClick={openNew}
          className="bg-pink-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-pink-600 flex items-center gap-1"
        >
          <Plus className="w-4 h-4" /> Add FAQ
        </button>
      </div>

      {/* Edit / Create modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="font-bold text-lg">{editing === 'new' ? 'New FAQ' : 'Edit FAQ'}</h2>
              <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    {CATEGORIES.map(c => (
                      <option key={c} value={c}>{c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                    <input
                      type="number"
                      value={form.sort_order}
                      onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm pb-2">
                    <input
                      type="checkbox"
                      checked={form.is_published}
                      onChange={e => setForm(f => ({ ...f, is_published: e.target.checked }))}
                      className="rounded border-gray-300"
                    />
                    Published
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
                <input
                  value={form.question}
                  onChange={e => setForm(f => ({ ...f, question: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="e.g. How do I place a bulk order?"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
                <textarea
                  value={form.answer}
                  onChange={e => setForm(f => ({ ...f, answer: e.target.value }))}
                  rows={6}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="Write the answer here..."
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setEditing(null)}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={() => saveMut.mutate(form)}
                  disabled={!form.question.trim() || !form.answer.trim() || saveMut.isPending}
                  className="bg-pink-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-pink-600 disabled:opacity-50"
                >
                  {saveMut.isPending ? 'Saving...' : 'Save FAQ'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FAQ List */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : faqs.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <HelpCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No FAQs yet</p>
          <p className="text-xs mt-1">Click "Add FAQ" to create your first one</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([category, items]) => (
            <div key={category} className="bg-white rounded-lg border">
              <div className="px-4 py-3 border-b bg-gray-50">
                <h3 className="font-bold text-sm text-gray-700 capitalize">{category.replace(/_/g, ' ')}</h3>
              </div>
              <div className="divide-y">
                {items.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(faq => (
                  <div key={faq.id} className="px-4 py-3 flex items-start gap-3 hover:bg-gray-50 group">
                    <GripVertical className="w-4 h-4 text-gray-300 mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm text-gray-900">{faq.question}</p>
                        {!faq.is_published && (
                          <span className="px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-500 rounded font-medium">Draft</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{faq.answer}</p>
                      <p className="text-[10px] text-gray-400 mt-1">
                        Order: {faq.sort_order || 0} · Helpful: {faq.helpful_count || 0}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <button
                        onClick={() => openEdit(faq)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                        title="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => { if (confirm('Delete this FAQ?')) deleteMut.mutate(faq.id) }}
                        className="p-1.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
